import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
} from 'react';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';

interface PendingApprovalsContextValue {
  pendingCount: number | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const PendingApprovalsContext = createContext<
  PendingApprovalsContextValue | undefined
>(undefined);

export const PendingApprovalsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const identityApi = useApi(identityApiRef);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const configApi = useApi(configApiRef);
  const baseUrl = configApi.getString('backend.baseUrl');

  const fetchPendingApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const identity = await identityApi.getBackstageIdentity();
      const credentials = await identityApi.getCredentials();

      if (!credentials.token) {
        throw new Error('No authentication token available');
      }

      const decoded = JSON.parse(atob(credentials.token.split('.')[1])) as {
        originalEmail?: string;
        email?: string;
        [key: string]: any;
      };

      const email =
        decoded.originalEmail || decoded.email || identity.userEntityRef;

      if (!email) {
        throw new Error('Email not found in token or identity');
      }

      const response = await fetch(
        `${baseUrl}/api/custom-api/pending-approval?user_mail=${email}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${credentials.token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch pending approvals');
      }

      const data = await response.json();
      const count = Number(data?.rows?.[0]?.count ?? 0);
      setPendingCount(count);
      setError(null);
    } catch (err: any) {
      setError(err.message);
      setPendingCount(null);
    } finally {
      setLoading(false);
    }
  }, [identityApi]);

  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  return (
    <PendingApprovalsContext.Provider
      value={{
        pendingCount,
        loading,
        error,
        refresh: fetchPendingApprovals,
      }}
    >
      {children}
    </PendingApprovalsContext.Provider>
  );
};

export const usePendingApprovals = (): PendingApprovalsContextValue => {
  const context = useContext(PendingApprovalsContext);
  if (!context) {
    throw new Error(
      'usePendingApprovals must be used within a PendingApprovalsProvider',
    );
  }
  return context;
};
